import {
  type HistoryGroup,
  isAppointmentEntry,
} from "@/features/history/utils";
import {
  type History,
  HistoryEntryTypeEnum,
  type Opportunity,
} from "@repo/api";
import { Text } from "@repo/skapa";

import { AppointmentHistoryItem } from "./AppointmentHistoryItem";
import { CommunicationHistoryItem } from "./CommunicationHistoryItem";
import { ConsentHistoryItem } from "./ConsentHistoryItem";
import { DraftOrderHistoryItem } from "./DraftOrderHistoryItem";
import { FileHistoryItem } from "./FileHistoryItem";
import { HistoryListViewItem } from "./HistoryListViewItem";
import { InterestedServicesHistoryItem } from "./InterestedServicesHistoryItem";
import { OrderHistoryItem } from "./OrderHistoryItem";
import { PlannerHistoryItem } from "./PlannerHistoryItem";
import { PrecallHistoryItem } from "./PrecallHistoryItem";
import { QuotationHistoryItem } from "./QuotationHistoryItem";
import { TaskHistoryItem } from "./TaskHistoryItem";
import { TitleHistoryItem } from "./TitleHistoryItem";

interface BaseHistoryProps {
  history: History | HistoryGroup;
  opportunity: Opportunity;
}

type DefaultHistoryItemProps = BaseHistoryProps;

interface HistoryItemProps extends DefaultHistoryItemProps {
  allHistories: History[] | HistoryGroup[];
}

export const HistoryItem = ({
  history,
  opportunity,
  allHistories,
}: HistoryItemProps) => {
  if (isAppointmentEntry(history)) {
    return (
      <AppointmentHistoryItem
        history={history}
        opportunity={opportunity}
        dropInTaskId={history.details?.task_id}
      />
    );
  }
  if (
    history.entryType === HistoryEntryTypeEnum.ORDER ||
    history.entryType === HistoryEntryTypeEnum.ORDER_UPDATE ||
    history.entryType === HistoryEntryTypeEnum.ORDER_IMPORTED ||
    history.entryType === HistoryEntryTypeEnum.ORDER_DELETED ||
    history.entryType === HistoryEntryTypeEnum.WAITING_LIST_RESTOCKED
  ) {
    return <OrderHistoryItem history={history} />;
  }
  if (
    history.entryType === HistoryEntryTypeEnum.DRAFT_ORDER_IMPORTED ||
    history.entryType === HistoryEntryTypeEnum.DRAFT_ORDER_DELETED
  ) {
    return <DraftOrderHistoryItem history={history} />;
  }
  if (
    history.entryType === HistoryEntryTypeEnum.QUOTATION_ASSOCIATED ||
    history.entryType === HistoryEntryTypeEnum.QUOTATION_DELETED
  ) {
    return <QuotationHistoryItem history={history} />;
  }
  if (history.entryType === HistoryEntryTypeEnum.TASK_UPDATE) {
    return <TaskHistoryItem history={history} opportunity={opportunity} />;
  }
  if (
    history.entryType === HistoryEntryTypeEnum.PLANNER ||
    history.entryType === HistoryEntryTypeEnum.PLANNER_ASSOCIATED
  ) {
    return <PlannerHistoryItem history={history} opportunity={opportunity} />;
  }

  if (history.entryType === HistoryEntryTypeEnum.PRE_CALL) {
    return <PrecallHistoryItem history={history} />;
  }

  if (history.entryType === HistoryEntryTypeEnum.TITLE_UPDATE) {
    return <TitleHistoryItem history={history} />;
  }
  if (
    history.entryType === HistoryEntryTypeEnum.COMMUNICATION_FAILED ||
    history.entryType === HistoryEntryTypeEnum.COMMUNICATION_SENT
  ) {
    return <CommunicationHistoryItem history={history} />;
  }

  if (
    history.entryType === HistoryEntryTypeEnum.CUSTOMER_FOLLOWUP_CONSENT_OPT_OUT
  ) {
    return <ConsentHistoryItem history={history} />;
  }

  if (history.entryType === HistoryEntryTypeEnum.B2B_ENR_FULFILLMENT_UPDATE) {
    const isDraftOrderConverted = allHistories.find(
      (entry) =>
        entry.entryType === HistoryEntryTypeEnum.DRAFT_ORDER_CONVERTED_UPDATE &&
        entry.details?.order_number === history.details?.order_number
    );
    if (isDraftOrderConverted) {
      return <OrderHistoryItem history={history} />;
    } else {
      return <DraftOrderHistoryItem history={history} />;
    }
  }
  if (history.entryType === HistoryEntryTypeEnum.INTEREST_SERVICES_UPDATE) {
    return <InterestedServicesHistoryItem history={history} />;
  }
  if (
    history.entryType === HistoryEntryTypeEnum.FILE_UPLOADED ||
    history.entryType === HistoryEntryTypeEnum.FILE_DOWNLOADED ||
    history.entryType === HistoryEntryTypeEnum.FILE_SHARED ||
    history.entryType === HistoryEntryTypeEnum.FILE_UNSHARED
  ) {
    return <FileHistoryItem history={history} />;
  }

  return <DefaultHistoryItem history={history} opportunity={opportunity} />;
};

const DefaultHistoryItem = ({ history }: DefaultHistoryItemProps) => {
  return (
    <HistoryListViewItem
      history={history}
      control="default"
      title={history.title}
      description={
        <div>
          {history.body && <Text textStyle="Body.S">{history.body}</Text>}
          {history.note && <Text textStyle="Body.S">{history.note}</Text>}
        </div>
      }
    />
  );
};
